import React, {useEffect, useRef, useState} from 'react';
import {useGameStore} from '../../stores/gameStore';
import {useNpcStore} from '../../stores/npcStore';
import {abilityService} from '../../services/abilityService';
import {AbilityDTO, CombatLogAbilityUseDTO, CombatLogEntryDTO, EntityType} from '@hexcrawl/shared-utils';

export const LogPanel: React.FC = () => {
  const gameState = useGameStore((s) => s.gameState);
  const npcs = useNpcStore((s) => s.npcs);
  const activeCombat = gameState?.activeCombat;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [abilityCache, setAbilityCache] = useState<Map<string, AbilityDTO>>(new Map());

  const logEntries = activeCombat?.log || [];

  // Fetch abilities for log entries
  useEffect(() => {
    const abilityIds = new Set(logEntries.flatMap(e => e.abilityUsed?.abilityId || []));
    const missingIds = Array.from(abilityIds).filter(id => !abilityCache.has(id));
    
    if (missingIds.length === 0) return;

    let cancelled = false;
    Promise.all(missingIds.map(id => abilityService.getAbility(id).catch(() => null)))
      .then(abilities => {
        if (cancelled) return;
        setAbilityCache(prev => {
          const next = new Map(prev);
          abilities.forEach((ability, i) => {
            if (ability) next.set(missingIds[i], ability);
          });
          return next;
        });
      });

    return () => { cancelled = true; };
  }, [logEntries, abilityCache]);

  // Auto-scroll to bottom when new entries are added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logEntries.length]);

  if (!activeCombat) return null;

  const character = gameState?.character;

  // Helper to look up entity name by ID
  const getEntityName = (entityId: string): string => {
    const entityState = activeCombat.entityStates.find(e => e.entityId === entityId);

    if (entityState) {
      switch (entityState.entityType) {
        case EntityType.PLAYER:
          if (entityId === character?.id)
            return character.name;
          break;

        case EntityType.NPC: {
          const npc = npcs[entityId];
          if (npc) return npc.name;
          break;
        }

        case EntityType.ENEMY:
          const enemyState = activeCombat.enemyStates.find(e => e.entityId === entityId);
          if (enemyState) {
            return enemyState.name;
          }
          break;
      }
    }

    return entityId;
  };

  const getAbilityName = (abilityId: string): string => {
    const ability = abilityCache.get(abilityId);
    if (ability) {
      return ability.name;
    }
    return abilityId;
  }

  const renderRolls = (entry: CombatLogAbilityUseDTO | undefined) => {
    if (!entry || entry.targets.length === 0) return null;

    const hitsWithDetail = entry.targets.filter(t => t.hitRoll || t.damage > 0);
    if (hitsWithDetail.length === 0) return null;

    return (
      <div style={{ fontSize: '11px', color: '#aaa', marginLeft: '12px', marginTop: '2px' }}>
        {hitsWithDetail.map((hit, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {hit.hitRoll && (
              <>
                <span style={{ color: '#88aaff' }}>
                  Roll: {hit.hitRoll.roll}
                  {hit.hitRoll.modifiers.length > 0 && (
                    <span style={{ color: '#6688cc' }}>
                      {hit.hitRoll.modifiers.map((mod, i) => (
                        <span key={i}>{mod >= 0 ? '+' : ''}{mod}</span>
                      ))}
                    </span>
                  )}
                </span>
                <span style={{ color: '#888' }}>vs {hit.hitRoll.target}</span>
              </>
            )}
            {hit.damage > 0
              ? <span style={{ color: '#ff8888' }}>{hit.hitRoll ? '→ ' : ''}{getEntityName(hit.targetId)}: {hit.damage} damage</span>
              : <span style={{ color: '#888' }}>missed {getEntityName(hit.targetId)}</span>
            }
          </div>
        ))}
      </div>
    );
  };

  const renderAbilityUse = (entry: CombatLogEntryDTO) => {
    if (!entry.abilityUsed || entry.abilityUsed.targets.length === 0) return null;

    const isPlayer = character && entry.entityId === character.id;
    const targetNames = entry.abilityUsed.targets.map(t => getEntityName(t.targetId)).join(', ');
    const abilityName = getAbilityName(entry.abilityUsed.abilityId);

    return (
      <div style={{ fontSize: '11px', color: '#aaa', marginLeft: '12px', marginTop: '2px' }}>
        Used ability <span style={{ color: '#88c0ff' }}>{abilityName}</span> on <span style={{ color: isPlayer ? '#a0ffb0' : '#ffb0a0', fontWeight: 'bold' }}>{targetNames}</span>
      </div>
    );
  }

  // A consumable's flavor line is written in second person, so it reads as its own
  // detail line rather than after the actor's name.
  const renderItemUse = (entry: CombatLogEntryDTO) => {
    if (!entry.itemUsed) return null;

    return (
      <div style={{ fontSize: '11px', color: '#aaa', marginLeft: '12px', marginTop: '2px', fontStyle: 'italic' }}>
        {entry.itemUsed.useText}
      </div>
    );
  };

  return (
    <div className="combat-log-panel" style={{
      height: '100%',
      background: 'rgba(26, 20, 34, 0.92)',
      border: '2px solid #bb86fc',
      borderTop: 'none',
      borderLeft: 'none',
      borderRadius: '0 0 10px 0',
      padding: '12px',
      color: '#dddddd',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      boxSizing: 'border-box',
    }}>
      <h3 style={{ margin: 0, color: '#d5b8ff', fontSize: '16px' }}>Combat Log</h3>
      <div 
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}
      >
        {logEntries.length === 0 ? (
          <div style={{ color: '#aaa' }}>No actions yet.</div>
        ) : (
          logEntries.map((entry, i) => {
            const isPlayer = character && entry.entityId === character.id;
            const casterName = getEntityName(entry.entityId);
            return (
              <div key={i} style={{ borderBottom: '1px solid #333', paddingBottom: '4px' }}>
                <div style={{ fontSize: '14px' }}>
                  <span style={{ color: isPlayer ? '#a0ffb0' : '#ffb0a0', fontWeight: 'bold' }}>{casterName}</span> {entry.resultText}
                </div>
                {renderAbilityUse(entry)}
                {renderItemUse(entry)}
                {renderRolls(entry.abilityUsed)}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
